import React, { useState, useEffect, JSX, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  InputAdornment,
  Chip,
  Badge,
  Menu,
  MenuItem
} from '@mui/material';

import {
  ShoppingBag,
  Search as SearchIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Sort as SortIcon,
  ShoppingCart as ShoppingCartIcon,
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon 
} from '@mui/icons-material';

import api from '../services/api'; // Your existing API service

// Define TypeScript interfaces for our data structures
interface OutletItem {
  id: number;
  variant_id: number;
  title: string;
  sku: string;
  price: string;
  discount_price: string;
  image_url?: string;
  available_quantity: number;
  reserved_by?: string;
  expires_at?: string;
  created_at: string;
}

interface NotificationState {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

interface CheckoutSummaryModalProps {
  open: boolean;
  onClose: () => void;
  items: OutletItem[];
  phone: string;
}

// Calculate savings percentage
const calculateSavingsPercent = (originalPrice: string, discountPrice: string): number => {
  const original = parseFloat(originalPrice);
  const discount = parseFloat(discountPrice);
  return Math.round(((original - discount) / original) * 100);
};

// Calculate savings amount
const calculateSavingsAmount = (originalPrice: string, discountPrice: string): string => {
  const original = parseFloat(originalPrice);
  const discount = parseFloat(discountPrice);
  return (original - discount).toFixed(2);
};

// Format currency
const formatCurrency = (amount: string): string => {
  return `$${parseFloat(amount).toFixed(2)}`;
};

const OutletStorePage: React.FC = () => {
  const [items, setItems] = useState<OutletItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [phoneValid, setPhoneValid] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [reservingItemId, setReservingItemId] = useState<number | null>(null);
  const [notification, setNotification] = useState<NotificationState>({ 
    show: false, 
    message: '', 
    type: 'success' 
  });
  const [phoneDialogOpen, setPhoneDialogOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<OutletItem | null>(null);
  const [detailView, setDetailView] = useState<OutletItem | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [sortOption, setSortOption] = useState<string>('discount');
  const [reservedItems, setReservedItems] = useState<OutletItem[]>([]);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState<boolean>(false);
  const [cartCount, setCartCount] = useState<number>(0);
  
  // Load outlet items
  const fetchItems = async (showRefreshing = true): Promise<void> => {
    if (showRefreshing) setIsRefreshing(true);
    
    try {
      const response = await api.get('/api/outlet/list');
      setItems(response.data);
      setError(null);
      
      // Update detail view item if it exists
      if (detailView) {
        const updatedItem = response.data.find((item: OutletItem) => item.id === detailView.id);
        if (updatedItem) {
          setDetailView(updatedItem);
        }
      }
    } catch (err) {
      if (!items.length) { // Only show error if we don't have any items already
        setError('Failed to load outlet items. Please try again later.');
      } else {
        // Show toast for refresh failures but keep existing items
        setNotification({
          show: true,
          message: 'Unable to refresh items. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
      if (showRefreshing) {
        setIsRefreshing(false);
        
        // Show success notification for manual refreshes
        if (showRefreshing === true) {
          setNotification({
            show: true,
            message: 'Items refreshed successfully!',
            type: 'success'
          });
        }
      }
    }
  };

  // Component for checkout summary modal — ECPay 站內付 2.0 embedded credit card
  const CheckoutSummaryModal: React.FC<CheckoutSummaryModalProps> = ({ open, onClose, items, phone }) => {
    const [step, setStep] = useState<'review' | 'paying' | 'processing' | 'success' | 'failed'>('review');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [orderRef, setOrderRef] = useState<string>('');
    const [merchantTradeNo, setMerchantTradeNo] = useState<string>('');
    const [paymentError, setPaymentError] = useState<string>('');

    const totalAmount = items.reduce((sum, item) =>
      sum + parseFloat(item.discount_price), 0).toFixed(2);

    // Load ECPay JS SDK dynamically
    const loadEcpaySdk = (sdkUrl: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Remove old script if exists
        const old = document.getElementById('ecpay-sdk');
        if (old) old.remove();

        const script = document.createElement('script');
        script.id = 'ecpay-sdk';
        script.src = sdkUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load ECPay SDK'));
        document.head.appendChild(script);
      });
    };

    // Step 1: Confirm checkout → get token → show card form
    const handleConfirmAndPay = async (): Promise<void> => {
      setIsLoading(true);
      setPaymentError('');

      try {
        // Confirm checkout first (creates order)
        const checkoutResponse = await api.post('/api/outlet/confirm-checkout', {
          items: items.map(item => item.id),
          phone: phone,
          orderRef: `OL-${Date.now().toString().substr(-6)}`
        });

        const orderId = checkoutResponse.data.orderId;
        setOrderRef(orderId);

        // Clear cart
        setReservedItems([]);
        setCartCount(0);
        localStorage.removeItem('outletCart');
        fetchItems(false);

        // Get ECPG token
        const tokenResponse = await api.post(`/api/ecpg/${orderId}/get-token`, {
          customerPhone: phone
        });

        if (!tokenResponse.data.success) {
          throw new Error(tokenResponse.data.message || 'Failed to get payment token');
        }

        const { token, merchantTradeNo: tradeNo, sdkUrl } = tokenResponse.data;
        setMerchantTradeNo(tradeNo);

        // Load SDK and render card form
        await loadEcpaySdk(sdkUrl);

        setStep('paying');
        setIsLoading(false);

        // Wait for DOM to render the container
        setTimeout(() => {
          const ECPay = (window as any).ECPay;
          if (!ECPay) {
            setPaymentError('ECPay SDK not loaded');
            setStep('failed');
            return;
          }

          ECPay.createPayment(token, {
            layout: 'single',
            language: 'zh-TW',
            containerId: 'ecpg-payment-container',
            callback: async (result: any) => {
              console.log('[ECPG] SDK callback:', result);
              if (!result || !result.PayToken) {
                setPaymentError('付款已取消或失敗');
                setStep('failed');
                return;
              }
              // Got PayToken, create payment
              await handleCreatePayment(orderId, result.PayToken, tradeNo);
            }
          });
        }, 500);
      } catch (error: any) {
        console.error('Checkout/token error:', error);
        setPaymentError(error?.response?.data?.message || error?.message || '發生錯誤');
        setStep('failed');
        setIsLoading(false);
      }
    };

    // Step 4: Create payment with PayToken
    const handleCreatePayment = async (orderId: string, payToken: string, tradeNo: string): Promise<void> => {
      setStep('processing');
      try {
        const response = await api.post(`/api/ecpg/${orderId}/create-payment`, {
          payToken,
          merchantTradeNo: tradeNo
        });

        if (response.data.requiresRedirect && response.data.threeDURL) {
          // 3D Secure — redirect the browser (NOT SPA router)
          window.location.href = response.data.threeDURL;
          return;
        }

        if (response.data.success && response.data.paid) {
          setStep('success');
        } else {
          setPaymentError(response.data.message || '付款失敗');
          setStep('failed');
        }
      } catch (error: any) {
        console.error('CreatePayment error:', error);
        setPaymentError(error?.response?.data?.message || '付款處理失敗');
        setStep('failed');
      }
    };

    // Retry payment
    const handleRetry = () => {
      setStep('review');
      setPaymentError('');
      setOrderRef('');
      setMerchantTradeNo('');
    };

    return (
      <Dialog
        open={open}
        onClose={step === 'processing' ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="checkout-dialog-title"
        sx={{ '& .MuiDialogContent-root': { minHeight: '400px' } }}
      >
        <DialogTitle id="checkout-dialog-title">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {step === 'review' && '訂單摘要 Order Summary'}
              {step === 'paying' && '信用卡付款 Credit Card Payment'}
              {step === 'processing' && '處理中 Processing...'}
              {step === 'success' && '付款成功 Payment Successful'}
              {step === 'failed' && '付款失敗 Payment Failed'}
            </Typography>
            {step !== 'processing' && (
              <IconButton edge="end" aria-label="close" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Step: Review order */}
          {step === 'review' && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>訂單項目 Order Items</Typography>
              {items.map(item => (
                <Box key={item.id} sx={{ display: 'flex', mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
                  <Box sx={{ mr: 2, width: 60, height: 60, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <ShoppingBag sx={{ color: 'grey.400' }} />
                    )}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary">SKU: {item.sku}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>{formatCurrency(item.discount_price)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>{formatCurrency(item.price)}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Box sx={{ mt: 3, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2">電話 Phone:</Typography>
                    <Typography>{phone}</Typography>
                  </Box>
                </Paper>
              </Box>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">總計 Total:</Typography>
                <Typography variant="h6" fontWeight="bold">${totalAmount}</Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 3, mb: 1, py: 1.5 }}
                onClick={handleConfirmAndPay}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isLoading ? '準備付款中...' : '確認並付款 Confirm & Pay'}
              </Button>
              <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                將使用信用卡線上付款 (ECPay 站內付)
              </Typography>
            </Box>
          )}

          {/* Step: ECPay card form */}
          {step === 'paying' && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                請在下方輸入信用卡資訊完成付款
                <br />
                Please enter your credit card details below
              </Alert>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">訂單編號: {orderRef}</Typography>
                <Typography variant="body2" fontWeight="bold">NT${totalAmount}</Typography>
              </Box>
              {/* ECPay JS SDK renders card form here */}
              <Box
                id="ecpg-payment-container"
                sx={{
                  minHeight: 300,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  '& iframe': { width: '100%', minHeight: 280 }
                }}
              />
            </Box>
          )}

          {/* Step: Processing */}
          {step === 'processing' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={48} sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom>付款處理中...</Typography>
              <Typography variant="body2" color="text.secondary">
                請勿關閉此視窗 / Please do not close this window
              </Typography>
            </Box>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>付款成功！</Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                您的訂單 {orderRef} 已完成付款
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Payment successful! Your order {orderRef} has been confirmed.
              </Typography>
            </Box>
          )}

          {/* Step: Failed */}
          {step === 'failed' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {paymentError || '付款失敗，請稍後再試'}
              </Alert>
              {orderRef && (
                <Typography variant="body2" color="text.secondary" paragraph>
                  訂單編號: {orderRef}
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{ mt: 2 }}
              >
                重新嘗試 Try Again
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {step !== 'processing' && (
            <Button onClick={onClose}>
              {step === 'success' ? '關閉 Close' : '繼續購物 Continue Shopping'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };
  
  // Check for payment result from 3D Secure redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    const orderId = params.get('order');

    if (paymentStatus && orderId) {
      if (paymentStatus === 'success') {
        setNotification({
          show: true,
          message: `訂單 ${orderId} 付款成功！Payment successful!`,
          type: 'success'
        });
      } else {
        setNotification({
          show: true,
          message: `訂單 ${orderId} 付款${paymentStatus === 'failed' ? '失敗' : '發生錯誤'}`,
          type: 'error'
        });
      }
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Load items and cart from localStorage on initial load
  useEffect(() => {
    fetchItems(false); // Initial load, don't show refreshing state
    
    // Load cart from localStorage
    const savedCart = localStorage.getItem('outletCart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        if (cartData.items && Array.isArray(cartData.items)) {
          setReservedItems(cartData.items);
          setCartCount(cartData.items.length);
          setCustomerPhone(cartData.phone || '');
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
    
    // Set up refresh interval to keep items fresh
    const intervalId = setInterval(() => fetchItems(false), 60000); // Refresh every minute without indicator
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (reservedItems.length > 0 && customerPhone) {
      localStorage.setItem('outletCart', JSON.stringify({
        items: reservedItems,
        phone: customerPhone
      }));
    } else if (reservedItems.length === 0) {
      localStorage.removeItem('outletCart');
    }
  }, [reservedItems, customerPhone]);
  
  // Filter and sort items
  const filteredItems = React.useMemo(() => {
    // First filter by search query
    let result = items.filter(item => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        (item.title && item.title.toLowerCase().includes(query)) || 
        (item.sku && item.sku.toLowerCase().includes(query))
      );
    });
    
    // Then sort based on the selected option
    switch (sortOption) {
      case 'discount':
        result.sort((a, b) => {
          const discountA = calculateSavingsPercent(a.price, a.discount_price);
          const discountB = calculateSavingsPercent(b.price, b.discount_price);
          return discountB - discountA; // Higher discount first
        });
        break;
      case 'price-low':
        result.sort((a, b) => parseFloat(a.discount_price) - parseFloat(b.discount_price));
        break;
      case 'price-high':
        result.sort((a, b) => parseFloat(b.discount_price) - parseFloat(a.discount_price));
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      default:
        break;
    }
    
    return result;
  }, [items, searchQuery, sortOption]);
  
  // Open sort menu
  const handleSortMenuOpen = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setSortMenuAnchor(event.currentTarget);
  };
  
  // Close sort menu
  const handleSortMenuClose = (): void => {
    setSortMenuAnchor(null);
  };
  
  // Set sort option
  const handleSortSelect = (option: string): void => {
    setSortOption(option);
    handleSortMenuClose();
  };
  
  // Handle phone input change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const phone = e.target.value;
    setCustomerPhone(phone);
    
    // Basic phone validation (at least 8 digits)
    const phoneRegex = /^\d{8,}$/;
    setPhoneValid(phoneRegex.test(phone));
  };
  
  // Handle reservation process
  const handleReserveClick = (item: OutletItem): void => {
    setSelectedItem(item);
    setPhoneDialogOpen(true);
  };
  
  // Handle item click to show detail view
  const handleItemClick = (item: OutletItem): void => {
    setDetailView(item);
    // Save scroll position for when we return
    window.history.pushState({ item: item.id }, '', `#item-${item.id}`);
  };
  
  // Handle back button from detail view
  const handleBackFromDetail = (): void => {
    setDetailView(null);
    window.history.back();
  };
  
  // Listen for browser back button
  useEffect(() => {
    const handlePopState = (): void => {
      setDetailView(null);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Handle actual reservation and cart addition
  const handleReserveConfirm = async (): Promise<void> => {
    if (!phoneValid || !selectedItem) return;
  
    setReservingItemId(selectedItem.id);
    setPhoneDialogOpen(false);
  
    try {
      // Call backend to reserve this item
      const response = await api.post('/api/outlet/add-to-cart', {
        id: selectedItem.id,
        reservedBy: customerPhone // We're using phone instead of email now
      });
  
      if (response.data.success) {
        // Add the item to the reservedItems array
        setReservedItems(prevItems => [...prevItems, selectedItem]);
  
        // Show success notification
        setNotification({
          show: true,
          message: 'Item reserved! It has been added to your cart.',
          type: 'success'
        });
  
        // Update local item state to reflect reservation
        setItems(prevItems =>
          prevItems.map(item =>
            item.id === selectedItem.id
              ? {
                  ...item,
                  reserved_by: customerPhone,
                  expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString()
                }
              : item
          )
        );
  
        // Update cart count
        setCartCount(prev => prev + 1);
        
        // Close the detail view if needed
        if (detailView && detailView.id === selectedItem.id) {
          setDetailView(null);
        }
      }
    } catch (error: unknown) {
      // Type assertion or type checking for error
      let errorMessage = 'Failed to add item to cart. It might have already been reserved.';
      
      // Check if error is an axios error with response data
      if (error && typeof error === 'object') {
        const err = error as any; // Use type assertion for error object
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }
      }
      
      setNotification({
        show: true,
        message: errorMessage,
        type: 'error'
      });
      
      // Refresh items to update availability
      fetchItems(false);
    } finally {
      setReservingItemId(null);
    }
  };
  
  // Handle going to cart
  const handleGoToCart = (): void => {
    if (reservedItems.length === 0) {
      setNotification({
        show: true,
        message: 'Your cart is empty.',
        type: 'error'
      });
      return;
    }
  
    // Open the checkout summary modal
    setCheckoutModalOpen(true);
  };
  
  // Handle closing the checkout modal
  const handleCloseCheckoutModal = (): void => {
    setCheckoutModalOpen(false);
  };

  // Handle closing the phone dialog
  const handleClosePhoneDialog = (): void => {
    setPhoneDialogOpen(false);
    setSelectedItem(null);
  };
  
  // Handle notification close
  const handleNotificationClose = (event?: React.SyntheticEvent | Event, reason?: string): void => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, show: false });
  };
  
  // Handle manual refresh
  const handleRefresh = (): void => {
    fetchItems(true);
  };
  
  // Render item list view
  const renderItemListView = (): JSX.Element => (
    <Grid container spacing={2}>
      {filteredItems.map(item => {
        const savingsPercent = calculateSavingsPercent(item.price, item.discount_price);
        
        return (
          <Grid item xs={12} key={item.id}>
            <Paper 
              elevation={1} 
              sx={{ 
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              <Box sx={{ display: 'flex', height: '100%' }}>
                {/* Left: Image */}
                <Box 
                  sx={{ 
                    width: 120, 
                    height: 120, 
                    backgroundColor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleItemClick(item)}
                >
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.title} 
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <ShoppingBag sx={{ color: 'grey.400', fontSize: 40 }} />
                  )}
                </Box>
                
                {/* Right: Content */}
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 2 }}>
                  {/* Item Title and SKU */}
                  <Box sx={{ mb: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      component="h2" 
                      sx={{ fontWeight: 500, cursor: 'pointer' }}
                      onClick={() => handleItemClick(item)}
                    >
                      {item.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {item.sku}
                    </Typography>
                  </Box>
                  
                  {/* Pricing */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                      {formatCurrency(item.discount_price)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      component="span" 
                      color="text.secondary" 
                      sx={{ textDecoration: 'line-through', mr: 1 }}
                    >
                      {formatCurrency(item.price)}
                    </Typography>
                    <Chip 
                      label={`${savingsPercent}% OFF`}
                      size="small"
                      color="error"
                      sx={{ height: 24 }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Quantity: {item.available_quantity}
                  </Typography>
                  
                  {/* Action Button */}
                  <Box sx={{ mt: 'auto' }}>
                    <Button 
                      variant="contained" 
                      fullWidth
                      onClick={() => handleReserveClick(item)}
                      disabled={reservingItemId === item.id || item.available_quantity < 1}
                      startIcon={reservingItemId === item.id ? <CircularProgress size={20} /> : null}
                    >
                      {reservingItemId === item.id ? 'Reserving...' : 
                       item.available_quantity < 1 ? 'Out of Stock' : 'Add to Cart'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
  
  // Render Detail View
  const renderDetailView = (): JSX.Element | null => {
    if (!detailView) return null;
    
    const savingsPercent = calculateSavingsPercent(detailView.price, detailView.discount_price);
    const savingsAmount = calculateSavingsAmount(detailView.price, detailView.discount_price);
    
    return (
      <Box>
        {/* Back Button */}
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBackFromDetail}
          sx={{ mb: 2 }}
        >
          Back to all items
        </Button>
        
        {/* Product Image */}
        <Paper sx={{ mb: 2, p: 2, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {detailView.image_url ? (
              <img 
                src={detailView.image_url} 
                alt={detailView.title} 
                style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
              />
            ) : (
              <ShoppingBag sx={{ fontSize: 80, color: 'grey.400' }} />
            )}
          </Box>
        </Paper>
        
        {/* Product Info */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            {detailView.title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            SKU: {detailView.sku}
          </Typography>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Price and Discount */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="p" sx={{ fontWeight: 'bold', mr: 2 }}>
              {formatCurrency(detailView.discount_price)}
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              color="text.secondary" 
              sx={{ textDecoration: 'line-through' }}
            >
              {formatCurrency(detailView.price)}
            </Typography>
          </Box>
          
          {/* Savings Info */}
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              mb: 3, 
              backgroundColor: '#ffebee', 
              borderColor: '#ffcdd2' 
            }}
          >
            <Typography variant="subtitle1" color="error" sx={{ fontWeight: 500, mb: 1 }}>
              Outlet Savings
            </Typography>
            <Typography variant="body1" color="error.dark">
              You save ${savingsAmount} ({savingsPercent}% off)
            </Typography>
          </Paper>
          
          {/* Availability */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Availability
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500, 
                  color: detailView.available_quantity > 0 ? 'success.main' : 'error.main' 
                }}
              >
                {detailView.available_quantity > 0 
                  ? `${detailView.available_quantity} in stock` 
                  : 'Out of stock'}
              </Typography>
            </Box>
            {detailView.available_quantity <= 3 && detailView.available_quantity > 0 && (
              <Typography variant="caption" color="warning.main">
                Low stock - order soon!
              </Typography>
            )}
          </Box>
          
          {/* Add to Cart Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => handleReserveClick(detailView)}
            disabled={detailView.available_quantity <= 0 || reservingItemId === detailView.id}
            startIcon={reservingItemId === detailView.id ? <CircularProgress size={24} /> : null}
          >
            {reservingItemId === detailView.id 
              ? 'Reserving...' 
              : detailView.available_quantity > 0 ? 'Add to Cart' : 'Out of Stock'}
          </Button>
        </Paper>
        
        {/* Product Details */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Product Details
          </Typography>
          <Typography variant="body2" paragraph>
            This is an outlet item with special discounted pricing. All sales are final.
          </Typography>
          <Typography variant="body2">
            Limited quantities available. Reserve your item for 2 hours before checkout.
          </Typography>
        </Paper>
      </Box>
    );
  };
  
  return (
    <Box sx={{ pb: 4 }}>
      {/* App Bar */}
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Outlet Store
          </Typography>
          
          {!detailView && (
            <>
              <Chip 
                label="Special Deals" 
                color="error" 
                size="small" 
                sx={{ mr: 1 }}
              />
              <IconButton 
                onClick={handleRefresh}
                disabled={isRefreshing}
                size="small"
                aria-label="Refresh"
              >
                <RefreshIcon 
                  sx={{ 
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }} 
                />
              </IconButton>
              
              <IconButton 
                onClick={handleSortMenuOpen}
                size="small"
                aria-label="Sort options"
                aria-controls="sort-menu"
                aria-haspopup="true"
              >
                <SortIcon />
              </IconButton>
              
              <Menu
                id="sort-menu"
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={handleSortMenuClose}
              >
                <MenuItem 
                  selected={sortOption === 'discount'} 
                  onClick={() => handleSortSelect('discount')}
                >
                  Biggest Discount
                </MenuItem>
                <MenuItem 
                  selected={sortOption === 'price-low'} 
                  onClick={() => handleSortSelect('price-low')}
                >
                  Price: Low to High
                </MenuItem>
                <MenuItem 
                  selected={sortOption === 'price-high'} 
                  onClick={() => handleSortSelect('price-high')}
                >
                  Price: High to Low
                </MenuItem>
                <MenuItem 
                  selected={sortOption === 'newest'} 
                  onClick={() => handleSortSelect('newest')}
                >
                  Newest First
                </MenuItem>
                </Menu>
              
              <IconButton 
                color="inherit" 
                onClick={handleGoToCart}
                aria-label="View Cart"
              >
                <Badge badgeContent={cartCount} color="primary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </>
          )}
        </Toolbar>
        
        {/* Search Bar - Only show in list view */}
        {!detailView && (
          <Box sx={{ px: 2, pb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search outlet items..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => setSearchQuery('')}
                      edge="end"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        )}
      </AppBar>
      
      {/* Main Content */}
      <Container maxWidth="sm" sx={{ mt: 2 }}>
        {/* Controls - Only show in list view */}
        {!detailView && !loading && filteredItems.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Typography variant="body2" color="text.secondary">
              {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
        
        {/* Show saved cart notification if there are items */}
        {!loading && reservedItems.length > 0 && !detailView && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleGoToCart}>
                View Cart
              </Button>
            }
          >
            You have {reservedItems.length} item{reservedItems.length !== 1 ? 's' : ''} in your cart.
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading outlet items...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : detailView ? (
          renderDetailView()
        ) : filteredItems.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {searchQuery 
              ? "No items match your search. Try a different term or check back later." 
              : "No outlet items available at the moment. Please check back later."}
          </Alert>
        ) : (
          renderItemListView()
        )}
      </Container>
      
      {/* Phone Collection Dialog */}
      <Dialog open={phoneDialogOpen} onClose={handleClosePhoneDialog}>
        <DialogTitle>Reserve Your Item</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter your phone number to reserve this item for 2 hours. We'll hold it until you complete checkout.
          </DialogContentText>
          
          {selectedItem && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 2, width: 60, height: 60, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {selectedItem.image_url ? (
                    <img 
                      src={selectedItem.image_url} 
                      alt={selectedItem.title} 
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <ShoppingBag sx={{ color: 'grey.400' }} />
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2">{selectedItem.title}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ mr: 1 }}>
                      {formatCurrency(selectedItem.discount_price)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                      {formatCurrency(selectedItem.price)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            id="phone"
            label="Phone Number"
            type="tel"
            fullWidth
            variant="outlined"
            value={customerPhone}
            onChange={handlePhoneChange}
            error={customerPhone !== '' && !phoneValid}
            helperText={customerPhone !== '' && !phoneValid ? "Please enter a valid phone number (at least 8 digits)" : ""}
            placeholder="e.g. 09123456789"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePhoneDialog}>Cancel</Button>
          <Button 
            onClick={handleReserveConfirm} 
            variant="contained" 
            disabled={!phoneValid}
          >
            Reserve Now
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={notification.show} 
        autoHideDuration={6000} 
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.type} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Checkout Modal */}
      <CheckoutSummaryModal
        open={checkoutModalOpen}
        onClose={handleCloseCheckoutModal}
        items={reservedItems}
        phone={customerPhone}
      />
    </Box>
  );
};

export default OutletStorePage;